import {Card, CardBody, CardHeader, Typography} from "@material-tailwind/react";
import React, {useEffect, useState} from "react";
import {ServicePaymentGrid} from "./grid";
import {getServicePayments} from "../../services/service/service_payment";
import {CardFooterPaginator} from "../system/cardFooterPaginator";
import {ServicePaymentContext} from "../../context/service_payment";
import {PaginateContext} from "../../context/paginate";
import {ServicePaymentForm} from "./form";
import {
  appendServiceFilterParams,
  SERVICE_FILTER_DEFAULTS,
  ServiceFilterContainer
} from "../shared/filters/service_filter_container";

const DEFAULT_SORT_BY = "id";
const DEFAULT_ROWS_COUNT = 10;
const SORT_DESC = false;

function asNumber(value) {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
}

function getMilestoneList(data) {
  if (Array.isArray(data)) return data;

  return data?.serviceSettlementMilestoneList
    || data?.service_settlement_milestone_list
    || data?.serviceSettlementList
    || data?.milestones
    || data?.data
    || [];
}

function getSettlementStatus(milestones) {
  if (!milestones.length) return "Waiting for step";
  if (milestones.some(({status}) => status === "eligible")) return "Ready to settle";
  if (milestones.some(({status}) => status === "pending")) return "Waiting for step";
  if (milestones.every(({status}) => status === "settled")) return "Settled";
  if (milestones.every(({status}) => status === "cancel")) return "Canceled";
  if (milestones.every(({status}) => status === "settled" || status === "cancel")) return "Closed";

  return "Waiting for step";
}

function getServicePaymentType(milestones) {
  if (milestones.some(({payment_type}) => payment_type === "first")) return "first";
  if (milestones.some(({payment_type}) => payment_type === "total")) return "total";

  return "";
}

function sortMilestonesByStep(milestones) {
  return [...milestones].sort((a, b) => (
    asNumber(a.step_number || a.step) - asNumber(b.step_number || b.step)
  ));
}

function groupMilestonesByService(milestones) {
  const servicesById = milestones.reduce((serviceMap, milestone) => {
    const serviceId = milestone.service_id;
    const service = serviceMap.get(serviceId) || {
      ...milestone,
      id: serviceId,
      service_id: serviceId,
      total: 0,
      settled_amount: 0,
      canceled_amount: 0,
      remaining_amount: 0,
      two_step_payment: false,
      payment_type: "",
      milestones: [],
    };

    service.milestones.push(milestone);
    service.total += asNumber(milestone.total);

    if (milestone.status === "settled") {
      service.settled_amount += asNumber(milestone.total);
    }

    if (milestone.status === "cancel") {
      service.canceled_amount += asNumber(milestone.total);
    }

    if (milestone.status === "pending" || milestone.status === "eligible") {
      service.remaining_amount += asNumber(milestone.total);
    }

    service.settlement_status = getSettlementStatus(service.milestones);
    serviceMap.set(serviceId, service);

    return serviceMap;
  }, new Map());

  return Array.from(servicesById.values()).map(service => {
    const sortedMilestones = sortMilestonesByStep(service.milestones);
    const paymentType = getServicePaymentType(sortedMilestones);

    return {
      ...service,
      payment_type: paymentType,
      two_step_payment: paymentType === "first",
      milestones: sortedMilestones,
    };
  });
}

function sortServicePayments(servicePayments, sortBy, sortType) {
  const direction = sortType === false ? -1 : 1;

  return [...servicePayments].sort((a, b) => {
    const first = a?.[sortBy];
    const second = b?.[sortBy];

    if (first === second) return 0;
    if (first === null || first === undefined) return 1;
    if (second === null || second === undefined) return -1;

    if (typeof first === "number" && typeof second === "number") {
      return (first - second) * direction;
    }

    return String(first).localeCompare(String(second), "fa") * direction;
  });
}


export function ServicePaymentManagement() {

  const [servicePayments, setServicePayments] = useState([]);
  const [allServicePayments, setAllServicePayments] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [gridRowCount, setGridRowCount] = useState(DEFAULT_ROWS_COUNT);
  const [gridCurrentPage, setGridCurrentPage] = useState(1);
  const [gridSelectedRow, setGridSelectedRow] = useState(null);
  const [gridTotalRowCount, setGridTotalRowCount] = useState(0);
  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);
  const [sortType, setSortType] = useState(SORT_DESC);
  const [from, setFrom] = useState((gridCurrentPage - 1) * gridRowCount + 1);
  const [to, setTo] = useState((gridCurrentPage) * gridRowCount);
  const [lastDataRefresh, setLastDataRefresh] = useState(null);
  const [filters, setFilters] = useState(SERVICE_FILTER_DEFAULTS);

  useEffect(() => {
    if ((gridCurrentPage) * gridRowCount <= gridTotalRowCount) {
      setTo((gridCurrentPage) * gridRowCount);
    } else {
      setTo(gridTotalRowCount);
    }
    if (gridTotalRowCount === 0) {
      setFrom(0);
    } else {
      setFrom((gridCurrentPage - 1) * gridRowCount + 1);
    }
  }, [gridCurrentPage, gridRowCount, gridTotalRowCount]);

  async function getData(paginate) {
    const result = await getServicePayments(paginate);
    if (result.status === 200) {
      const milestoneList = getMilestoneList(result.data);
      const groupedServices = groupMilestonesByService(milestoneList);
      setAllServicePayments(groupedServices);
      setGridTotalRowCount(groupedServices.length);
    }
  }

  useEffect(() => {
    const paginateParams = new URLSearchParams();

    appendServiceFilterParams(paginateParams, filters);

    const paginate = paginateParams.toString();

    getData(paginate);
  }, [filters, lastDataRefresh]);

  useEffect(() => {
    const sortedServicePayments = sortServicePayments(allServicePayments, sortBy, sortType);
    const offset = (parseInt(gridCurrentPage, 10) - 1) * parseInt(gridRowCount, 10);
    const limit = offset + parseInt(gridRowCount, 10);

    setServicePayments(sortedServicePayments.slice(offset, limit));
    setGridTotalRowCount(sortedServicePayments.length);
  }, [allServicePayments, gridCurrentPage, gridRowCount, sortBy, sortType]);

  useEffect(() => {
    if (!gridSelectedRow?.service_id) return;

    const updatedSelectedRow = allServicePayments.find(
      ({service_id}) => service_id === gridSelectedRow.service_id
    );

    if (updatedSelectedRow) {
      setGridSelectedRow(updatedSelectedRow);
      return;
    }

    setGridSelectedRow(null);
    setModalOpen(false);
  }, [allServicePayments, gridSelectedRow?.service_id]);

  return (
    <ServicePaymentContext.Provider value={{
      servicePayments: servicePayments,
      setServicePayments: setServicePayments,
      servicePayment: gridSelectedRow,
      setServicePayment: setGridSelectedRow,
      open: modalOpen,
      setOpen: setModalOpen,
      setLastDataRefresh: setLastDataRefresh,
    }}>
      <PaginateContext.Provider value={{
        setSortBy: setSortBy,
        sortType: sortType,
        setSortType: setSortType,
        active: gridCurrentPage,
        setActive: setGridCurrentPage,
        totalRows: gridTotalRowCount,
        rowsCount: gridRowCount,
        setRowsCount: setGridRowCount,
        from: from,
        to: to,
      }}>
        <div className="flex w-full flex-col gap-4 p-4">
          <ServicePaymentForm/>

          <Card className="w-full pt-12">
            <CardHeader floated={true} shadow={false} className="rounded-none">
              <div className="mb-6 flex items-center justify-between gap-8">
                <Typography variant="h5" color="blue-gray">تسویه خدمات</Typography>
              </div>
            </CardHeader>

            <CardBody>
              <ServiceFilterContainer onFiltersChange={setFilters}/>
            </CardBody>
          </Card>

          <Card className="w-full">
            <CardBody className="overflow-x-auto w-full p-0">
              <ServicePaymentGrid/>
            </CardBody>

            <CardFooterPaginator />
          </Card>
        </div>
      </PaginateContext.Provider>
    </ServicePaymentContext.Provider>
  );
}
